import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ACCESS_KEY = "finlens_token";
const REFRESH_KEY = "finlens_refresh_token";

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}

export function setTokens({ token, refresh_token }) {
  if (token) localStorage.setItem(ACCESS_KEY, token);
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Access tokens are short-lived (30 min). When one expires mid-session, try
// the refresh token once, transparently, before giving up and forcing a
// re-login. Concurrent 401s share a single in-flight refresh call instead of
// each firing their own (which would race to consume the same one-time-use
// refresh token and fail for everyone but the first).
let refreshPromise = null;

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) throw new Error("No refresh token");
  // Plain axios call here, not `api` — avoids recursing through this same
  // interceptor if the refresh endpoint itself ever returns a 401.
  const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
  setTokens(data);
  return data.token;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isAuthEndpoint = config?.url?.includes("/auth/login") || config?.url?.includes("/auth/signup") || config?.url?.includes("/auth/refresh");

    if (response?.status === 401 && !config._retried && !isAuthEndpoint) {
      config._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        config.headers.Authorization = `Bearer ${newToken}`;
        return api(config);
      } catch {
        clearTokens();
        // Let the original 401 propagate so the app can route to /auth.
      }
    }
    return Promise.reject(error);
  }
);

// FastAPI's `detail` is a plain string for HTTPException, but a list of
// {msg, loc, ...} objects for Pydantic 422 validation errors. Use this
// anywhere an error gets shown to the user so both shapes render readably.
export function errorMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(", ") || fallback;
  return detail || fallback;
}
